/**
 * The replay origin: the scan mesh as it enters the first checkpoint step,
 * before any tool operation is applied. Replay reconstructs every later step by
 * cloning this base and re-applying the operation log on top.
 *
 * Heavy geometry is held in a module ref (not Zustand) to avoid serializing GPU
 * buffers into devtools/persist — mirroring `meshSnapshot.js`.
 */
import * as THREE from 'three';

/** @type {{ geometry: import('three').BufferGeometry, position: number[], quaternion: number[], scale: number[], meshName: string } | null} */
let basePayload = null;

/**
 * Store a clone of the given mesh as the replay base.
 * @param {import('three').Mesh} mesh
 * @returns {boolean}
 */
export function captureBaseMesh(mesh) {
  if (!mesh?.geometry?.attributes?.position) return false;
  basePayload?.geometry?.dispose?.();
  basePayload = {
    geometry: mesh.geometry.clone(),
    position: mesh.position.toArray(),
    quaternion: mesh.quaternion.toArray(),
    scale: mesh.scale.toArray(),
    meshName: mesh.name || 'ShadedMesh',
  };
  return true;
}

export function hasBaseMesh() {
  return basePayload != null;
}

/**
 * Build a fresh editable mesh from the stored base (caller owns disposal).
 * @param {import('three').Material} material
 * @returns {import('three').Mesh | null}
 */
export function instantiateBaseMesh(material) {
  if (!basePayload) return null;
  const mesh = new THREE.Mesh(basePayload.geometry.clone(), material);
  mesh.name = basePayload.meshName;
  mesh.position.fromArray(basePayload.position);
  mesh.quaternion.fromArray(basePayload.quaternion);
  mesh.scale.fromArray(basePayload.scale);
  mesh.matrixAutoUpdate = true;
  mesh.updateMatrixWorld(true);
  return mesh;
}

export function clearBaseMesh() {
  basePayload?.geometry?.dispose?.();
  basePayload = null;
}
